import { memo, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDashboardAssessments } from "@/hooks/useDashboardAssessments";
import { WelcomeBanner } from "./WelcomeBanner";
import { DashboardTile } from "./DashboardTile";
import { PrerequisiteOverlay } from "./PrerequisiteOverlay";
import { RecommendedNextStepContent } from "./tiles/RecommendedNextStepContent";
import { ScoreTileContent } from "./tiles/ScoreTileContent";
import { InsightsTileContent } from "./tiles/InsightsTileContent";
import { PlanAssemblyFlow } from "@/components/plan-assembly/PlanAssemblyFlow";
import { AssessmentTileContent } from "./tiles/AssessmentTileContent";
import {
  ArrowRight, BarChart2, Lightbulb, ClipboardList, Calendar,
  Building2, DollarSign, TrendingUp, Users, PiggyBank,
  GitBranch, Shield, FileText, BookOpen,
} from "lucide-react";

interface TileConfig {
  title: string;
  description: string;
  icon: any;
  moduleId?: string;
}

const TILE_CONFIGS: TileConfig[] = [
  { title: "Recommended Next Step", icon: ArrowRight, description: "Your personalised next action" },
  { title: "mySuccession Score", icon: BarChart2, description: "Your overall succession readiness score" },
  { title: "Succession Insights", icon: Lightbulb, description: "Risks and opportunities across your plan" },
  { title: "mySuccession Snapshot", icon: ClipboardList, description: "Your personal and professional readiness baseline", moduleId: "owner-readiness" },
  { title: "Readiness & Exit Timeline", icon: Calendar, description: "Your readiness score and target transition date", moduleId: "owner-readiness-exit-timeline" },
  { title: "Business Profile", icon: Building2, description: "Your practice structure, size and client profile", moduleId: "business-profile" },
  { title: "Business Valuation", icon: DollarSign, description: "Your estimated practice value and methodology", moduleId: "business-valuation" },
  { title: "Business Optimization Review", icon: TrendingUp, description: "Key opportunities to strengthen your practice", moduleId: "business-optimization" },
  { title: "Advisory Team", icon: Users, description: "The professional advisors supporting your succession", moduleId: "advisory-team" },
  { title: "Financial Readiness", icon: PiggyBank, description: "Your personal financial readiness for transition", moduleId: "financial-readiness" },
  { title: "mySuccession Strategy", icon: GitBranch, description: "Your chosen succession path and successor profile", moduleId: "successor-strategy" },
  { title: "Governance & Family", icon: Shield, description: "Governance structures and family alignment", moduleId: "governance-family" },
  { title: "Exit Structure & Tax Optimization", icon: FileText, description: "Tax-efficient exit structure for your transition", moduleId: "exit-structure" },
  { title: "mySuccession Plan Assembly", icon: BookOpen, description: "Your comprehensive succession plan", moduleId: "mysuccession-plan" },
];

const MODULE_TITLES_MAP: Record<string, string> = {
  "owner-readiness": "mySuccession Snapshot",
  "owner-readiness-exit-timeline": "Readiness & Exit Timeline",
  "business-profile": "Business Profile",
  "business-valuation": "Business Valuation",
  "business-optimization": "Business Optimization Review",
  "advisory-team": "Advisory Team",
  "financial-readiness": "Financial Readiness",
  "successor-strategy": "mySuccession Strategy",
  "governance-family": "Governance & Family",
  "exit-structure": "Exit Structure & Tax Optimization",
  "mysuccession-plan": "mySuccession Plan Assembly",
};

interface DashboardGridProps {
  onScoreClick?: () => void;
  onInsightsClick?: () => void;
  onModuleSelect?: (moduleId: string, title: string) => void;
  onTimelineEdit?: () => void;
}

// Map completed module IDs to their post-completion route
const COMPLETED_MODULE_ROUTES: Record<string, string> = {
  "advisory-team": "/team",
  "mysuccession-plan": "/plan",
};

export const DashboardGrid = memo(function DashboardGrid({ onScoreClick, onInsightsClick, onModuleSelect, onTimelineEdit }: DashboardGridProps) {
  const { getTileStatus, getIncompletePrereqs, firstIncompleteModule } = useDashboardAssessments();
  const [prereqOverlay, setPrereqOverlay] = useState<{ moduleId: string; prereqs: string[] } | null>(null);
  const [planAssemblyOpen, setPlanAssemblyOpen] = useState(false);
  const navigate = useNavigate();

  const handleLockedClick = useCallback((moduleId: string) => {
    const prereqs = getIncompletePrereqs(moduleId);
    setPrereqOverlay({ moduleId, prereqs });
  }, [getIncompletePrereqs]);

  return (
    <div className="space-y-6">
      <WelcomeBanner onModuleSelect={onModuleSelect} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0">
        {/* 1. mySuccession Score */}
        {/* 1. mySuccession Score */}
        <div
          onClick={onScoreClick}
          className="relative flex h-[280px] cursor-pointer flex-col p-6"
          style={{
            background: "hsl(var(--primary))",
            borderRadius: "12px 0 12px 12px",
          }}
        >
          <ScoreTileContent onViewDetails={onScoreClick} />
        </div>

        {/* 2. Succession Insights — borderless floating tile */}
        <div
          onClick={onInsightsClick}
          className="flex h-[280px] cursor-pointer flex-col p-6 transition-all duration-200 hover:bg-transparent"
          style={{ background: "transparent", border: "none", boxShadow: "none" }}
        >
          <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: "#0d9488" }}>
            Succession Insights
          </span>
          <p className="mt-1 text-xs text-muted-foreground">
            We've found risks and opportunities across your plan
          </p>
          <div className="mt-4 flex items-center justify-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full border-2" style={{ borderColor: "#0d9488" }}>
              <Lightbulb className="h-6 w-6" style={{ color: "#0d9488" }} />
            </div>
          </div>
          <div className="mt-3 flex-1 flex flex-col items-center justify-center">
            <InsightsTileContent />
          </div>
        </div>

        {/* 3. Next Recommended Action — light purple */}
        <div
          onClick={() => {
            const mod = firstIncompleteModule;
            const title = MODULE_TITLES_MAP[mod] ?? mod;
            if (onModuleSelect) onModuleSelect(mod, title);
          }}
          className="relative flex h-[280px] cursor-pointer flex-col rounded-xl bg-purple-50 p-6"
        >
          <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: "hsl(var(--primary))" }}>
            Next Recommended Action
          </span>
          <p className="mt-1 text-xs" style={{ color: "hsl(var(--primary) / 0.7)" }}>Your personalised next action</p>
          <div className="mt-4 flex-1">
            <RecommendedNextStepContent onModuleSelect={onModuleSelect} />
          </div>
        </div>
      </div>

      <h2 className="text-lg font-semibold text-muted-foreground">Build your plan through guided steps</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {TILE_CONFIGS.filter(c => !!c.moduleId).map((cfg) => {
          const status = getTileStatus(cfg.moduleId!);
          const handleNavigate = () => {
            if (status === "complete") {
              if (cfg.moduleId === "owner-readiness-exit-timeline") {
                onTimelineEdit?.();
                return;
              }
              const route = COMPLETED_MODULE_ROUTES[cfg.moduleId!];
              if (route) {
                navigate(route);
              }
              return;
            }
            if (cfg.moduleId === "mysuccession-plan") {
              setPlanAssemblyOpen(true);
              return;
            }
            onModuleSelect?.(cfg.moduleId!, cfg.title);
          };

          return (
            <DashboardTile
              key={cfg.title}
              title={cfg.title}
              description={cfg.description}
              icon={cfg.icon}
              status={status}
              onNavigate={handleNavigate}
              onLockedClick={() => handleLockedClick(cfg.moduleId!)}
            >
              {status === "complete" && <AssessmentTileContent moduleId={cfg.moduleId!} />}
            </DashboardTile>
          );
        })}
      </div>

      {prereqOverlay && (
        <PrerequisiteOverlay
          open
          onClose={() => setPrereqOverlay(null)}
          lockedModuleId={prereqOverlay.moduleId}
          incompletePrereqs={prereqOverlay.prereqs}
        />
      )}

      <PlanAssemblyFlow
        open={planAssemblyOpen}
        onClose={() => setPlanAssemblyOpen(false)}
      />
    </div>
  );
});

DashboardGrid.displayName = "DashboardGrid";
